"use client";

import React, {JSX, useId} from "react";

import {useSnapshot} from "valtio";

import {useScrollIntoView} from "@/shared/hooks/dom/use-scroll-into-view";
import {useTranslation} from "@/shared/i18n";
import {type RadioButtonInstance, type RadioOption} from "@/shared/models/form/fields";

import {type FormFieldSize} from "./types";

import "./styles/radio-field.scss";

interface RadioFieldHelpers {
    isSelected: boolean;
    isDisabled: boolean;
    inputProps: {
        id?: string;
        type: "radio";
        name: string;
        value?: string;
        checked: boolean;
        disabled: boolean;
        onChange: () => void;
        onBlur: () => void;
        onFocus: () => void;
        className?: string;
    };
}

interface RadioFieldProps<T = string, O extends RadioOption<T> = RadioOption<T>> {
    model: RadioButtonInstance<T>;
    label?: string;
    className?: string;
    size?: FormFieldSize;
    direction?: "horizontal" | "vertical";
    children?: (option: O, helpers: RadioFieldHelpers) => React.ReactNode;
}

export function RadioField<T = string, O extends RadioOption<T> = RadioOption<T>>({
    model,
    label,
    className,
    size,
    direction = "horizontal",
    children,
}: RadioFieldProps<T, O>): JSX.Element {
    const snapshot = useSnapshot(model);
    const ref = useScrollIntoView<HTMLDivElement>(model);
    const groupId = useId();
    const {t} = useTranslation(); // For option labels (various namespaces)
    const {t: tValidation} = useTranslation("validation"); // For error messages

    // Helper to get translated label
    const getOptionLabel = (option: O): string => {
        return option.labelKey ? t(option.labelKey as Parameters<typeof t>[0], option.labelParams) : option.label;
    };
    const sizeClass = size ? `radio-field--${size}` : "";

    // Custom rendering with minimal wrapper for styling
    if (children) {
        return (
            <div className={className} role="radiogroup" aria-label={label}>
                {(model.options as Array<O>).map((option: O, index: number) => {
                    const isSelected = model.isSelected(option);
                    const isDisabled = model.isOptionDisabled(option);

                    const inputProps = {
                        type: "radio" as const,
                        name: `radio-group-${groupId}`,
                        checked: isSelected,
                        disabled: isDisabled,
                        onChange: () => model.setValue(option.value),
                        onBlur: () => model.onBlur(),
                        onFocus: () => model.onFocus(),
                    };

                    const helpers: RadioFieldHelpers = {
                        isSelected,
                        isDisabled,
                        inputProps,
                    };

                    const element = children(option, helpers);
                    // If it's a valid React element, clone it with key
                    if (React.isValidElement(element)) {
                        return React.cloneElement(element, {key: index} as React.Attributes);
                    }
                    // Fallback: wrap in div if not a valid element
                    return <div key={index}>{element}</div>;
                })}
            </div>
        );
    }

    // Default rendering with full wrapper and styling
    return (
        <div ref={ref} className={`radio-field ${sizeClass} ${className || ""}`}>
            {label && (
                <label className="radio-field__label">
                    {label} {model.isRequired && <span className="radio-field__required">*</span>}
                </label>
            )}

            <div className={`radio-field__group radio-field__group--${direction}`}>
                {(model.options as Array<O>).map((option: O, index: number) => {
                    const isSelected = model.isSelected(option);
                    const isDisabled = model.isOptionDisabled(option);
                    const optionId = `${groupId}-option-${index}`;

                    const inputProps = {
                        id: optionId,
                        type: "radio" as const,
                        name: `radio-group-${groupId}`,
                        value: String(option.value),
                        checked: isSelected,
                        disabled: isDisabled,
                        onChange: () => model.setValue(option.value),
                        onBlur: () => model.onBlur(),
                        onFocus: () => model.onFocus(),
                        className: "radio-field__input",
                    };

                    return (
                        <label
                            key={index}
                            htmlFor={optionId}
                            className={`radio-field__option ${isSelected ? "is-selected" : ""} ${isDisabled ? "is-disabled" : ""}`}
                        >
                            <input {...inputProps} />
                            <span className="radio-field__custom-radio"></span>
                            <span className="radio-field__option-label">{getOptionLabel(option)}</span>
                        </label>
                    );
                })}
            </div>

            {snapshot.shouldShowError && snapshot.error && (
                <span className="radio-field__error-text">
                    {tValidation(snapshot.error.message as Parameters<typeof tValidation>[0], snapshot.error.messageParams)}
                </span>
            )}
        </div>
    );
}
